<template>
  <v-container class="pa-2 page-layout" fluid>
    <v-row dense>
      <v-col class="py-0">
        <h1 class="page-title">Patient Lookup</h1>
      </v-col>
    </v-row>

    <!-- Seleção de insurance (só para roles que escolhem; herda a lógica do Patients) -->
    <v-row v-if="!isFamilyHealthChoiceTenant" dense>
      <v-col cols="12" md="6" lg="4" class="pb-0">
        <v-select
          v-model="insChoice"
          :items="insurances"
          label="Choose Insurance"
          outlined
          dense
          :disabled="insDisabled"
        ></v-select>
      </v-col>
    </v-row>

    <v-row v-if="needsInsurance" dense>
      <v-col class="pt-0 pb-0">
        <v-alert type="info" dense class="py-2">Please select an insurance to continue</v-alert>
      </v-col>
    </v-row>

    <template v-else>
      <!-- Formulário de identificação -->
      <v-card flat class="rounded-lg pa-4 mb-4">
        <div class="body-2 grey--text mb-3">
          Search by <strong>Member ID</strong>, or by <strong>First name + Last name + Date of birth</strong>.
        </div>

        <v-row dense>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="memberId"
              label="Member ID"
              placeholder="Member ID"
              clearable
              outlined
              dense
              hide-details="auto"
              @keyup.enter="submit"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="text-center my-2 grey--text text--darken-1 body-2">— or —</div>

        <v-row dense>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="firstName"
              label="First name"
              :disabled="!!memberIdTrimmed"
              clearable
              outlined
              dense
              hide-details="auto"
              @keyup.enter="submit"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="lastName"
              label="Last name"
              :disabled="!!memberIdTrimmed"
              clearable
              outlined
              dense
              hide-details="auto"
              @keyup.enter="submit"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              :value="dobText"
              label="Date of birth"
              placeholder="MM/DD/YYYY"
              :disabled="!!memberIdTrimmed"
              clearable
              outlined
              dense
              hide-details="auto"
              inputmode="numeric"
              maxlength="10"
              @input="onDobInput"
              @keyup.enter="submit"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense class="mt-2">
          <v-col cols="12">
            <v-btn color="primary" :disabled="!canSearch || loading" :loading="loading" @click="submit">
              <v-icon left>mdi-magnify</v-icon> Search
            </v-btn>
            <v-btn text class="ml-2" :disabled="loading" @click="reset">Clear</v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Colisão: pede member id -->
      <v-alert v-if="collision" type="warning" dense class="mb-4">
        More than one patient matches those details. Please provide the Member ID to identify the patient.
      </v-alert>

      <!-- Não encontrado -->
      <v-alert v-if="searched && !collision && !patient && !loading" type="info" dense class="mb-4">
        No patient found with those criteria.
      </v-alert>

      <!-- Resultado: uma pessoa -->
      <v-card v-if="patient" flat class="rounded-lg">
        <v-card-text>
          <v-row align="center" dense>
            <v-col cols="12" md="8">
              <div class="d-flex align-center">
                <span class="text-h6">{{ patient.NAME }}</span>
                <v-chip
                  :color="statusColor(patient)"
                  small
                  label
                  class="ml-3 white--text"
                >
                  {{ statusLabel(patient) }}
                </v-chip>
              </div>
              <div class="body-2 grey--text">
                ID {{ patient.ID }} &nbsp;•&nbsp; DOB {{ formatDate(patient.DOB) }}
                &nbsp;•&nbsp; {{ patient.SEX || 'N/A' }}
              </div>
              <div class="body-2 grey--text">
                {{ formatPlanName(patient.PLAN_NAME) || 'N/A' }}
                <span v-if="patient.PCP"> &nbsp;•&nbsp; PCP: {{ patient.PCP }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-md-right">
              <patient-modal-fhc v-if="isFamilyHealthChoiceTenant" :patient="patient" />
              <patient-modal v-else :patient="patient" :insurance="insChoice" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import axios from 'axios';
import PatientModal from '../components/PatientModal.vue';
import PatientModalFHC from '../components/PatientModalFHC.vue';
import { formatPlanNameForDisplay } from '../utils/planNameDisplay';

export default {
  name: 'PatientLookup',
  components: {
    PatientModal,
    'patient-modal-fhc': PatientModalFHC,
  },
  data() {
    return {
      user: '',
      insurances: [],
      insChoice: [],
      memberId: '',
      firstName: '',
      lastName: '',
      dobText: '',
      patient: null,
      collision: false,
      searched: false,
      loading: false,
    };
  },
  mounted() {
    this.getUser();
  },
  computed: {
    // Mesma detecção de tenant do Patients.vue.
    isFamilyHealthChoiceTenant() {
      const c = (process.env.VUE_APP_CUSTOMER || '').toLowerCase();
      const ins = (this.insChoice || '').toString().toLowerCase();
      return c === 'familyhealthchoice' || c === 'floridahealthchoice'
        || ins === 'familyhealthchoice' || ins === 'floridahealthchoice';
    },
    insDisabled() {
      return this.user.role === 'Provider';
    },
    needsInsurance() {
      return !this.isFamilyHealthChoiceTenant
        && (this.insChoice === null || this.insChoice === undefined || this.insChoice.length === 0);
    },
    memberIdTrimmed() {
      return (this.memberId || '').trim();
    },
    // Converte o texto MM/DD/YYYY digitado em ISO (YYYY-MM-DD), ou '' se inválido.
    // É o `dob` real usado no gate e enviado ao backend.
    dobIso() {
      const t = (this.dobText || '').trim();
      const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!m) return '';
      const mm = m[1].padStart(2, '0');
      const dd = m[2].padStart(2, '0');
      const yyyy = m[3];
      if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return '';
      const iso = `${yyyy}-${mm}-${dd}`;
      // Rejeita datas impossíveis (ex.: 02/30/2000).
      const d = new Date(`${iso}T00:00:00Z`);
      if (Number.isNaN(d.getTime()) || d.getUTCDate() !== Number(dd)) return '';
      return iso;
    },
    // Gate: member id sozinho, OU nome+sobrenome+DOB válido.
    canSearch() {
      if (this.memberIdTrimmed) return true;
      return !!((this.firstName || '').trim() && (this.lastName || '').trim() && this.dobIso);
    },
  },
  methods: {
    formatPlanName(val) {
      return formatPlanNameForDisplay(val);
    },
    async getUser() {
      await axios.get('/api/user')
        .then((response) => { this.user = response.data[0]; })
        .catch(() => this.$router.push('/login'));
      await this.loadInsurances();
    },
    // Espelha getInsurances/setPresets do Patients.vue para os roles relevantes.
    async loadInsurances() {
      if (this.isFamilyHealthChoiceTenant) {
        this.insurances = ['FamilyHealthChoice'];
        this.insChoice = 'FamilyHealthChoice';
        return;
      }
      const { role } = this.user;
      if (role === 'Provider' || role === 'Clinical' || role === 'Group' || role === 'Affiliation') {
        this.insurances = this.user.insurance || [];
        if (Array.isArray(this.user.insurance) && this.user.insurance.length) {
          this.insChoice = this.user.insurance[0];
        }
        return;
      }
      if (role === 'Admin' || role === 'Superuser') {
        await axios.get('api/list/insurances').then((response) => {
          this.insurances = response.data;
        });
      }
    },
    // Máscara MM/DD/YYYY enquanto digita: insere as barras sozinho.
    onDobInput(val) {
      const digits = (val || '').replace(/\D/g, '').slice(0, 8);
      let out = digits;
      if (digits.length > 4) {
        out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
      } else if (digits.length > 2) {
        out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }
      this.dobText = out;
    },
    reset() {
      this.memberId = '';
      this.firstName = '';
      this.lastName = '';
      this.dobText = '';
      this.patient = null;
      this.collision = false;
      this.searched = false;
    },
    async submit() {
      if (!this.canSearch || this.loading) return;
      this.loading = true;
      this.patient = null;
      this.collision = false;
      this.searched = false;

      const params = {};
      if (this.memberIdTrimmed) {
        params.memberId = this.memberIdTrimmed;
      } else {
        params.firstName = this.firstName.trim();
        params.lastName = this.lastName.trim();
        params.dob = this.dobIso;
      }

      try {
        const res = await axios.get(`api/patient/lookup/${this.insChoice}`, { params });
        this.searched = true;
        if (res.data && res.data.found) {
          this.patient = res.data.patient;
        }
      } catch (e) {
        this.searched = true;
        if (e.response && e.response.status === 409) {
          this.collision = true;
        }
      } finally {
        this.loading = false;
      }
    },
    // Status do paciente — mesma lógica do Patients.vue (Active/Termed/etc).
    statusColor(patient) {
      if (String(patient.PORTAL_ACCOUNT_STATUS || '').toLowerCase() === 'suspended') return 'error';
      const billing = String(patient.MEMBER_BILLING_BANNER_KIND || 'none');
      if (billing === 'plan_suspended_nonpay') return 'error';
      if (billing === 'payment_grace') return 'warning';
      const today = new Date().toISOString().substring(0, 7);
      const eligible = patient.ELIGIBLE_MONTH ? String(patient.ELIGIBLE_MONTH).substring(0, 7) : null;
      const termed = patient.DISENROLLMENT_DATE ? String(patient.DISENROLLMENT_DATE).substring(0, 7) : null;
      if (!eligible) return 'grey';
      if (!termed) return eligible <= today ? 'success' : 'grey';
      return eligible <= today && termed > today ? 'success' : 'error';
    },
    statusLabel(patient) {
      if (String(patient.PORTAL_ACCOUNT_STATUS || '').toLowerCase() === 'suspended') return 'Account suspended';
      const billing = String(patient.MEMBER_BILLING_BANNER_KIND || 'none');
      if (billing === 'plan_suspended_nonpay') return 'Plan suspended (non-payment)';
      if (billing === 'payment_grace') return 'Payment overdue (grace period)';
      const today = new Date().toISOString().substring(0, 7);
      const eligible = patient.ELIGIBLE_MONTH ? String(patient.ELIGIBLE_MONTH).substring(0, 7) : null;
      const termed = patient.DISENROLLMENT_DATE ? String(patient.DISENROLLMENT_DATE).substring(0, 7) : null;
      if (!eligible) return 'Unknown';
      if (!termed) return eligible <= today ? 'Active' : 'Not yet eligible';
      return eligible <= today && termed > today ? 'Active' : 'Termed';
    },
    formatDate(val) {
      if (!val) return 'N/A';
      const str = String(val).trim();
      const iso = str.includes('T') ? str.split('T')[0] : str.substring(0, 10);
      const parts = iso.split('-').filter(Boolean);
      if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
      return iso;
    },
  },
};
</script>
